import {
  Box,
  Space,
  SpaceVertical,
  Spinner,
  ToggleSwitch,
  Tooltip,
  TreeItem,
  useToggle,
  WindowedTreeCollection,
  type WindowedTreeNodeProps,
} from '@looker/components';
import { BIIcon, BIIncludeExcludeListItem, BISearchBar, globalTheme } from '@pulsepointinc/bi-components';
import { DsTab, DsTabBar, DsTypography } from '@pulsepointinc/design-system-react';
import debounce from 'lodash/debounce';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import DotLoader from '../../../../../../components/DotLoader';
import { SmartSearchChecker } from '../../../../../../components/SmartSearchChecker';
import { type ICustomFilter, MIN_SEARCH_PHRASE_LENGTH, SEARCH_DEBOUNCE_TIMEOUT } from '../../../../../../helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import type { HierarchyItem } from '../../../../../../lib/api/hierarchies/dto';
import type { Filter } from '../../../../../../stores/filters';
import { useSubGroupFilterState } from '../../../hooks/useSubGroupFilterState';
import { FilterSearchNoResults } from '../../FilterSearchNoResults/Index';
import type { IncludeExcludeTab } from '../IncludeExcludeFilter/types';
import { TreeIncludeExcludeHeaderSection } from './components/TreeIncludeExcludeHeaderSection';
import { TreeLoader } from './components/TreeLoader';
import { findNodeAndParent, getCascadingTaxonomyIds, isExpandableTreeNode, stateMap } from './helpers';
import { useTreeIncludeExclude } from './hooks/useTreeIncludeExclude';
import { StyledErrorLabel, StyledTree } from './styles';
import type { TreeNode } from './types';

const mapTreeNodeToExpression = (treeNodes: HierarchyItem[], currentMode: string | undefined): string => {
  return treeNodes.map((i) => `${currentMode === 'block' ? '-' : ''}${i.alias}`).join(',');
};

type TreeIncludeExcludeFilterProps = Omit<ICustomFilter, 'filterName'> & {
  filterName: string;
  dictionary: 'MeSH' | 'IAB';
  decileFilterName: string;
  recencyFilterName: string;
};

export const TreeIncludeExcludeFilter: FC<TreeIncludeExcludeFilterProps> = ({
  dictionary,
  decileFilterName,
  filterName,
  onChange,
  recencyFilterName,
}) => {
  const { filters } = useFilters();
  const expression = filters[filterName]?.expression ?? '';

  const [searchPhrase, setSearchPhrase] = useState('');
  const [clickedTaxonomyID, setClickedTaxonomyID] = useState('');
  const [ephemeralTaxonomyIds, setEphemeralTaxonomyIds] = useState<string[]>([]);
  const { value: advancedSearchEnabled, toggle } = useToggle(false);
  const [currentTab, setCurrentTab] = useState<IncludeExcludeTab>('all');

  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length >= MIN_SEARCH_PHRASE_LENGTH) {
          setSearchPhrase(value);
        } else {
          setSearchPhrase('');
        }
      }, SEARCH_DEBOUNCE_TIMEOUT),
    [],
  );

  const parsedValues = useMemo(
    () =>
      ({
        items: expression
          .split(',')
          .map((t) => t.replace(/^-/, ''))
          .filter(Boolean),
        mode: expression ? (expression.startsWith('-') ? 'block' : 'allow') : undefined,
      }) as const,
    [expression],
  );

  const handleChangeTab = (tab: IncludeExcludeTab) => {
    if (tab === 'all') setEphemeralTaxonomyIds([]);
    setCurrentTab(tab);
  };

  const {
    categoriesTree,
    isFetching,
    treeMode,
    selectedTreeItems,
    errorMessage,
    setCategoriesTree,
    setSelectedTreeItems,
  } = useTreeIncludeExclude(clickedTaxonomyID, dictionary, parsedValues, searchPhrase, advancedSearchEnabled);

  const { handleChange } = useSubGroupFilterState(filterName, onChange);

  const handleTreeItemClick = useCallback(
    (item: TreeNode) => {
      if (searchPhrase) return;

      setClickedTaxonomyID(item.taxonomy_id);

      setCategoriesTree((currentTree) => {
        const { node } = findNodeAndParent(item.taxonomy_id, currentTree);

        if (node) {
          node.isOpen = !node.isOpen;
          node.isLoading = Object.values(node.children).length === 0;
        }

        return currentTree;
      });
    },
    [searchPhrase, setCategoriesTree],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: relevant dependencies are added
  useEffect(() => {
    if (!expression) return;

    const parsedValues = {
      items: expression?.split(',').map((t) => t.replace(/^-/, '')),
      mode: expression.startsWith('-') ? 'block' : 'allow',
    } as const;

    const expressionType = parsedValues.mode === 'allow' ? 'target' : 'block';

    handleChange({
      filterName,
      expression,
      enabled: true,
      expressionType,
    });
  }, []);

  const handleCategoryToggle = useCallback(
    (taxonomy_id: TreeNode['taxonomy_id'], type: 'check' | 'block') => {
      if (currentTab === 'selected') {
        setEphemeralTaxonomyIds((prev) => {
          const newIds = getCascadingTaxonomyIds(taxonomy_id);
          const newIdsToAdd = newIds.filter((id) => !prev.includes(id));
          return [...prev, ...newIdsToAdd];
        });
      }

      setCategoriesTree((currentTree) => {
        const mode = type === 'check' ? 'allow' : 'block';
        const { node, ancestors } = findNodeAndParent(taxonomy_id, currentTree);

        if (!node) {
          return currentTree;
        }

        const filter: Pick<Filter, 'filterName' | 'enabled'> = {
          filterName,
          enabled: true,
        };

        // Unselecting
        if (node.state === mode) {
          setSelectedTreeItems((treeItems) => {
            const withoutClickedNode = treeItems.filter((i) => i.taxonomy_id !== node.taxonomy_id);

            treeMode.current = withoutClickedNode.length > 0 ? mode : undefined;

            const hasClickedNodeDescendants = withoutClickedNode.some((x) => {
              return getCascadingTaxonomyIds(x.taxonomy_id).includes(node.taxonomy_id);
            });
            node.state = hasClickedNodeDescendants ? `semi-${mode}` : undefined;

            ancestors.forEach((ancestor) => {
              const hasDescendants = withoutClickedNode.some((x) => x.taxonomy_id.includes(ancestor.taxonomy_id));

              ancestor.state = hasDescendants ? ancestor.state : undefined;
            });

            handleChange({
              ...filter,
              expressionType: treeMode.current === 'allow' ? 'target' : 'block',
              expression: mapTreeNodeToExpression(withoutClickedNode, treeMode.current),
            });

            return withoutClickedNode;
          });
        } else {
          // Selecting
          node.state = mode;
          treeMode.current = mode;

          ancestors.forEach((ancestor) => {
            ancestor.state = ancestor.state ?? `semi-${mode}`;
          });

          const newSelected = [...selectedTreeItems, node];
          setSelectedTreeItems(newSelected);

          handleChange({
            ...filter,
            expressionType: mode === 'allow' ? 'target' : 'block',
            expression: mapTreeNodeToExpression(newSelected, treeMode.current),
          });
        }

        return { ...currentTree };
      });
    },
    [filterName, handleChange, selectedTreeItems, setCategoriesTree, setSelectedTreeItems, treeMode, currentTab],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: relevant dependencies are added
  const generateTree = useCallback(
    (item: TreeNode): WindowedTreeNodeProps => {
      const isExpandable = isExpandableTreeNode(item);

      const label = (
        <BIIncludeExcludeListItem
          state={item.state && stateMap[item.state]}
          disabledBlocking={treeMode.current === 'allow'}
          disabledChecking={treeMode.current === 'block'}
          category={item.descriptor}
          onToggleCategory={(_, type) => handleCategoryToggle(item.taxonomy_id, type)}
        />
      );

      const content = isExpandable ? (
        <StyledTree label={label} onClick={() => handleTreeItemClick(item)} />
      ) : (
        <TreeItem itemRole="none">{label}</TreeItem>
      );

      return {
        content,
        isOpen: item.isOpen || currentTab === 'selected',
        items:
          item.isLoading && Object.values(item.children).length === 0
            ? [
                {
                  content: (
                    <TreeItem>
                      <Space gap="u2">
                        <DsTypography>Loading...</DsTypography>
                        <DotLoader />
                      </Space>
                    </TreeItem>
                  ),
                },
              ]
            : isExpandable
              ? Object.values(item.children)
                  .filter((i) => {
                    if (currentTab === 'selected') return !!i.state || ephemeralTaxonomyIds.includes(i.taxonomy_id);
                    if (searchPhrase) return i.isSearchResult;
                    return true;
                  })
                  .map(generateTree)
              : undefined,
      };
    },
    [
      currentTab,
      handleCategoryToggle,
      handleTreeItemClick,
      searchPhrase,
      selectedTreeItems,
      treeMode,
      ephemeralTaxonomyIds,
    ],
  );

  const trees = useMemo(() => {
    const visible = Object.values(categoriesTree).filter((i) => {
      if (currentTab === 'selected') return !!i.state || ephemeralTaxonomyIds.includes(i.taxonomy_id);
      if (searchPhrase) return i.isSearchResult;
      return true;
    });

    return visible.sort((a, b) => a.alias.localeCompare(b.alias, undefined, { sensitivity: 'base' })).map(generateTree);
  }, [categoriesTree, currentTab, generateTree, searchPhrase, ephemeralTaxonomyIds]);

  return (
    <SpaceVertical gap="u5" height="100%">
      <TreeIncludeExcludeHeaderSection
        decileFilterName={decileFilterName}
        recencyFilterName={recencyFilterName}
        onChange={handleChange}
      />
      <SpaceVertical gap="none" flex={1} minHeight={0}>
        <DsTabBar
          layout="panel"
          value={currentTab}
          onValueChange={(e) => handleChangeTab(e.target.value as IncludeExcludeTab)}
        >
          <DsTab value="all" label="All Categories" />
          <DsTab value="selected" label={`Selected Only (${selectedTreeItems.length})`} />
        </DsTabBar>
        <SpaceVertical flex={1} minHeight={0} px="u5">
          {currentTab === 'all' &&
            (!trees.length && !searchPhrase && isFetching ? (
              <TreeLoader />
            ) : (
              <SpaceVertical height="100%" gap={'none'} align="stretch">
                <Box zIndex={10} backgroundColor="white" paddingTop="u3" paddingBottom="2px" position="sticky" top={0}>
                  <BISearchBar
                    defaultValue={searchPhrase}
                    onSearchChange={handleSearch}
                    placeholder="Search"
                    showClearButton
                    after={
                      <SmartSearchChecker>
                        <Space gap="u2">
                          {isFetching && <Spinner />}
                          <Tooltip placement="top" content={'AI-powered Smart search'}>
                            <Space gap="u1" align="center" color={globalTheme.colors.textSecondary}>
                              <BIIcon icon="SmartSearch" />
                              <ToggleSwitch on={advancedSearchEnabled} onChange={toggle} />
                            </Space>
                          </Tooltip>
                        </Space>
                      </SmartSearchChecker>
                    }
                  />
                  {errorMessage && (
                    <StyledErrorLabel noActions intent="warn">
                      {errorMessage}
                    </StyledErrorLabel>
                  )}
                </Box>

                {!trees.length && searchPhrase && !isFetching && !errorMessage ? (
                  <FilterSearchNoResults />
                ) : (
                  <WindowedTreeCollection width={'100%'} trees={trees} />
                )}
              </SpaceVertical>
            ))}
          {currentTab === 'selected' && (
            <WindowedTreeCollection style={{ paddingTop: '12px' }} width={'100%'} trees={trees} />
          )}
        </SpaceVertical>
      </SpaceVertical>
    </SpaceVertical>
  );
};
